import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Form, Tooltip } from 'antd';

// Import Components:
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import OnTraccrButton from '../common/buttons/OnTraccrButton';

// Import Actions:
import { approveRejectForm, getFormById } from './state/forms.actions';
import { constructFormPayloadForAPI, getErrorsFromResponses, prepareResponsePayload, validateTimeEntryTableResponses } from './ResponderHelpers';
import FormTextInput from '../common/inputs/FormTextInput';

import { safeExtractFromObject } from '../helpers/helpers';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

// CONSTANTS:
export const APPROVE_FORM_SUBMISSION = 'Approve';
export const REJECT_FORM_SUBMISSION = 'Reject';

/** Form Approval Button Component */
function FormApprovalButton({
  formId,
  type,
  style = {},
  redirectToHome = null,
  updateData,
  collectedData,
  sections,
  responses,
  errorMap,
  isViewMode = false,
  shouldCalculateErrors = false,
  sectionPermissionMap,
  showIcon = false,
}) {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const formApprovals = useSelector((state) => state.forms.approvalSummary);
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));

  const [loading, setLoading] = useState(false);

  const hasRedesign = settings?.uxRedesignPt1;

  let id = '';
  if (hasRedesign) {
    id = `button-redesign-${type === APPROVE_FORM_SUBMISSION ? 'approve' : 'reject'}`;
  } else {
    id = `form-${type === APPROVE_FORM_SUBMISSION ? 'approval' : 'reject'}-button`;
  }

  const RejectionNote = (
    <Form form={form} initialValues={{ note: null }}>
      <FormTextInput
        name="note"
        isNotDisplay
        placeholder="Enter a rejection message"
        textProps={{
          autoComplete: 'off',
          textarea: true,
          rows: 4,
        }}
      />
    </Form>
  );

  const isButtonDisabled = Object.keys(errorMap || {}).length > 0 && !isViewMode;

  const showModal = useCallback((e) => async (buttonType) => {
    e.stopPropagation();
    if (isButtonDisabled) return;
    let calculatedErrorMap = errorMap;

    if (shouldCalculateErrors) {
      setLoading(true);
      const selectedForm = await dispatch(getFormById(formId, { getRecentDraft: true }));
      if (selectedForm) {
        const selectedFormResponses = selectedForm.data?.sections?.reduce((acc, { fields = [] }) => {
          fields?.forEach((field) => {
            acc[field.fieldId] = field.response;
          });
          return acc;
        }, {});
        const { errorMap: newErrorMap } = getErrorsFromResponses({
          sections,
          responses: selectedFormResponses,
          sectionPermissionMap,
          companySettings: settings,
          isApproval: true,
        });
        calculatedErrorMap = newErrorMap;
      }
      setLoading(false);
    }

    const {
      [formId]: {
        id: approvalId,
        templateName: name,
        number,
      } = {},
    } = formApprovals;
    const approved = buttonType === APPROVE_FORM_SUBMISSION;
    const title = number
      ? `${buttonType} Form: ${name} #${number}?` : `${buttonType} Form: ${name}?`;

    let validationText = null;

    if (approved && !validateTimeEntryTableResponses({ sections, responses })) {
      validationText = 'A time entry exceeds the warning threshold of hours configured for this form. Please review the entries and make sure they are correct.';
    }

    if (Object.keys(calculatedErrorMap).length > 0) {
      validationText = 'You have required fields that are not filled out. Please review the fields and make sure they are correct.';
    }

    CustomConfirmModal({
      title,
      okText: 'Confirm',
      cancelText: 'Cancel',
      content: !approved
        ? (
          <>
            {validationText && (
              <div style={{ marginBottom: 10 }}>
                {validationText}
              </div>
            )}
            {RejectionNote}
          </>
        )
        : validationText,
      async onOk() {
        let note = form.getFieldValue('note');
        if (note === '') note = null;
        let formData = {};
        if (updateData) {
          const payload = prepareResponsePayload({
            ...updateData,
            collectedData: collectedData ?? updateData.collectedData,
          });
          formData = await constructFormPayloadForAPI({
            form: payload,
            addSectionId: true,
          });
        }
        await dispatch(approveRejectForm(formId, {
          approvalId,
          approved,
          data: formData.data,
          files: formData.files,
          note,
        }));
        if (redirectToHome) {
          redirectToHome();
        }
      },
    });
  }, [
    formId,
    formApprovals,
    updateData,
    collectedData,
    sections,
    responses,
    isButtonDisabled,
    shouldCalculateErrors,
    sectionPermissionMap,
    settings,
    errorMap,
  ]);

  const buttonIcon = useMemo(() => {
    if (showIcon) {
      if (type === APPROVE_FORM_SUBMISSION ) {
        return <CheckOutlined />;
      }
      if (type === REJECT_FORM_SUBMISSION) {
        return <CloseOutlined />;
      }
    }
    return null;
  }, [showIcon]);

  // If there are errors, disable the approval button
  const Button = useMemo(() => (
    <OnTraccrButton
      title={type}
      id={id}
      style={{ margin: 2, ...style }}
      onClick={(e) => showModal(e)(type)}
      loading={loading}
      icon={buttonIcon}
      iconLeft
    />
  ), [type, id, style, showModal, loading]);

  if (isButtonDisabled) {
    return (
      <Tooltip
        title="You have required fields that are not filled out. Please fill out all required fields."
        placement="left"
      >
        {Button}
      </Tooltip>
    )
  }

  return Button;
}

FormApprovalButton.propTypes = {
  formId: PropTypes.string.isRequired,
  type: PropTypes.oneOf([APPROVE_FORM_SUBMISSION, REJECT_FORM_SUBMISSION]).isRequired,
  style: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ])),
  redirectToHome: PropTypes.func,
  updateData: PropTypes.shape({}),
  collectedData: PropTypes.shape({}),
  sections: PropTypes.arrayOf(PropTypes.shape({})),
  responses: PropTypes.shape({}),
  errorMap: PropTypes.shape({}),
  isViewMode: PropTypes.bool,
  shouldCalculateErrors: PropTypes.bool,
  sectionPermissionMap: PropTypes.shape({}),
};

FormApprovalButton.defaultProps = {
  style: {},
  redirectToHome: null,
  updateData: undefined,
  collectedData: undefined,
  sections: [],
  responses: {},
  errorMap: {},
  isViewMode: false,
  shouldCalculateErrors: false,
  sectionPermissionMap: {},
};

export default FormApprovalButton;
